import React, { useState } from 'react';
import { useOCPP } from '../contexts/OCPPContext';

const EmergencyControl = () => {
  const { chargers, emergencyStop, emergencyStopAll, emergencyActions } = useOCPP();
  const [selectedCharger, setSelectedCharger] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('zero_power');
  const [loading, setLoading] = useState(false);

  const emergencyMethods = [
    { value: 'zero_power', label: 'Zero Power Profile', description: 'Set charging limit to 0W with high priority' },
    { value: 'stop_transaction', label: 'Stop Transaction', description: 'Send RemoteStopTransaction command' },
    { value: 'reset_soft', label: 'Soft Reset', description: 'Restart charger software' },
    { value: 'reset_hard', label: 'Hard Reset', description: 'Full charger reboot' }
  ];

  const handleEmergencyStop = async () => {
    const confirmMessage = selectedCharger === 'all' 
      ? `Are you sure you want to execute ${selectedMethod} emergency stop on ALL chargers?`
      : `Are you sure you want to execute ${selectedMethod} emergency stop on charger ${selectedCharger}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      if (selectedCharger === 'all') {
        await emergencyStopAll(selectedMethod);
        alert('Emergency stop executed on all chargers');
      } else {
        await emergencyStop(selectedCharger, selectedMethod);
        alert(`Emergency stop executed on charger ${selectedCharger}`);
      }
    } catch (error) {
      alert(`Emergency stop failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const chargerArray = Object.values(chargers);
  const connectedChargers = chargerArray.filter(c => c.connection_status === 'Connected');

  return (
    <div>
      <h1>Emergency Control Center</h1>
      
      <div className="emergency-panel card">
        <h3>Emergency Stop Controls</h3>
        
        <div className="grid grid-2" style={{ marginBottom: '1.5rem' }}>
          <div>
            <label htmlFor="charger-select" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Select Charger:
            </label>
            <select 
              id="charger-select"
              value={selectedCharger} 
              onChange={(e) => setSelectedCharger(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                border: '1px solid #ddd', 
                borderRadius: '4px' 
              }}
            >
              <option value="">Select a charger...</option>
              <option value="all">ALL CHARGERS</option>
              {connectedChargers.map(charger => (
                <option key={charger.id} value={charger.id}>
                  {charger.id} ({charger.vendor} {charger.model})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="method-select" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Emergency Method:
            </label>
            <select 
              id="method-select"
              value={selectedMethod} 
              onChange={(e) => setSelectedMethod(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                border: '1px solid #ddd', 
                borderRadius: '4px' 
              }}
            >
              {emergencyMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px' }}>
          <strong>Selected Method:</strong> {emergencyMethods.find(m => m.value === selectedMethod)?.label}
          <br />
          <span style={{ color: '#666' }}>
            {emergencyMethods.find(m => m.value === selectedMethod)?.description}
          </span>
        </div>

        <button
          onClick={handleEmergencyStop}
          disabled={!selectedCharger || loading}
          className="btn btn-danger"
          style={{ 
            width: '100%', 
            padding: '1rem', 
            fontSize: '1.1rem', 
            fontWeight: 'bold' 
          }}
        >
          {loading ? 'Executing Emergency Stop...' : 'EXECUTE EMERGENCY STOP'}
        </button>
      </div>

      <div className="card">
        <h3>Connected Chargers Status</h3>
        {connectedChargers.length === 0 ? (
          <p>No chargers are currently connected.</p>
        ) : (
          <div className="grid grid-3">
            {connectedChargers.map(charger => {
              const activeConnectors = charger.connectors?.filter(c => 
                c.status === 'Occupied' || c.status === 'Charging'
              ) || [];
              
              return (
                <div key={charger.id} className="card" style={{ margin: 0 }}>
                  <h4>{charger.id}</h4>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    {charger.vendor} {charger.model}
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <strong>{activeConnectors.length}</strong> active transactions
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    Last seen: {charger.last_heartbeat ? 
                      new Date(charger.last_heartbeat).toLocaleString() : 
                      'Never'
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card">
        <h3>Emergency Action History</h3>
        {emergencyActions.length === 0 ? (
          <p>No emergency actions have been executed yet.</p>
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {emergencyActions.slice().reverse().map(action => (
              <div 
                key={action.id} 
                className={`card ${action.status === 'success' ? 'success' : 'error'}`}
                style={{ margin: '0.5rem 0' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <strong>
                      {action.method} on {action.chargerId}
                    </strong>
                    <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                      {action.status === 'success' ? '✓ Success' : '✗ Failed'}
                      {action.error && `: ${action.error}`}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>
                    {new Date(action.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergencyControl;